import { formatDate } from "@/utils/formatDate";
import type { DeadlineStatus, KpiActionOrigin, Parcel, ParcelOwner } from "./types";

export function deadlineClasses(status: DeadlineStatus): string {
  switch (status) {
    case "overdue": return "bg-red-600 text-white";
    case "soon": return "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300";
    case "approaching": return "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300";
    case "ok": return "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300";
    default: return "text-gray-400";
  }
}

export function deadlineLabel(status: DeadlineStatus): string {
  switch (status) {
    case "overdue": return "Po terminie";
    case "soon": return "≤ 14 dni";
    case "approaching": return "≤ 30 dni";
    case "ok": return "W terminie";
    default: return "—";
  }
}

export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("pl-PL");
  } catch {
    return iso;
  }
}

export function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pl-PL");
  } catch {
    return iso;
  }
}

/** Just the time-of-day (HH:MM) — for timelines where the day is shown in a header. */
export function fmtTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString("pl-PL", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

/**
 * Human-friendly day heading for grouped timelines, e.g. "Dzisiaj · 02.06.2026"
 * or "wtorek · 01.06.2026". Expects a YYYY-MM-DD day key.
 */
export function fmtDayHeading(day: string): string {
  try {
    const d = new Date(`${day}T00:00:00`);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diff = Math.round((today.getTime() - d.getTime()) / 86_400_000);
    const date = formatDate(day, day);
    if (diff === 0) return `Dzisiaj · ${date}`;
    if (diff === 1) return `Wczoraj · ${date}`;
    const weekday = d.toLocaleDateString("pl-PL", { weekday: "long" });
    return `${weekday} · ${date}`;
  } catch {
    return day;
  }
}

/** Whole calendar days from `iso` (date or datetime) to today, in local time.
 *  Positive = in the past, negative = in the future, null when unparsable. */
export function daysSince(iso: string | null | undefined, now: Date = new Date()): number | null {
  if (!iso) return null;
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso);
  if (Number.isNaN(d.getTime())) return null;
  const a = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const b = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((b.getTime() - a.getTime()) / 86_400_000);
}

/** Polish plural for "projekt": 1 → projekt, 2-4 → projekty, reszta → projektów
 *  (z wyjątkiem nastek: 12-14 idą jak „projektów"). */
export function plProjekty(n: number): string {
  const abs = Math.abs(n);
  if (abs === 1) return `${n} projekt`;
  const last = abs % 10;
  const teens = abs % 100;
  if (last >= 2 && last <= 4 && !(teens >= 12 && teens <= 14)) return `${n} projekty`;
  return `${n} projektów`;
}

/** Polish plural for "dzień": 1 → dzień, otherwise dni. */
export function plDni(n: number): string {
  return Math.abs(n) === 1 ? `${n} dzień` : `${n} dni`;
}

/** Relative day label next to a date: "dzisiaj", "wczoraj", "12 dni temu",
 *  "za 3 dni". Empty string when the date is missing. */
export function daysAgoLabel(iso: string | null | undefined, now: Date = new Date()): string {
  const n = daysSince(iso, now);
  if (n == null) return "";
  if (n === 0) return "dzisiaj";
  if (n === 1) return "wczoraj";
  if (n < 0) return `za ${plDni(-n)}`;
  return `${plDni(n)} temu`;
}

export function fmtArea(m2: number | null | undefined): string {
  if (m2 == null) return "—";
  return `${m2.toLocaleString("pl-PL", { maximumFractionDigits: 0 })} m²`;
}

/** Kwota w PLN — backend przysyła Decimal jako string, żeby nic nie zgubić na float. */
export function fmtMoney(value: string | number | null | undefined): string {
  if (value == null || value === "") return "—";
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return "—";
  return `${n.toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} zł`;
}

/** Recursively collect string/number leaves from a DRF error payload. Field
 *  errors can nest arbitrarily (arrays, per-field dicts, list-serializer index
 *  maps) — flattening only one level leaves objects that stringify to
 *  "[object Object]", so we descend and keep just the primitive messages. */
function collectMessages(value: unknown, out: string[]): void {
  if (value == null) return;
  if (typeof value === "string") {
    if (value.trim()) out.push(value);
  } else if (typeof value === "number") {
    out.push(String(value));
  } else if (Array.isArray(value)) {
    for (const v of value) collectMessages(v, out);
  } else if (typeof value === "object") {
    for (const v of Object.values(value)) collectMessages(v, out);
  }
}

/** Extract a human-readable message from a failed API call. The backend wraps
 *  errors as { data, errors: { detail | field: [...] }, meta }. */
export function apiErr(err: unknown, fallback = "Wystąpił błąd. Spróbuj ponownie."): string {
  const errs = (err as { response?: { data?: { errors?: Record<string, unknown> } } })
    ?.response?.data?.errors;
  if (!errs) return fallback;
  if (typeof errs.detail === "string") return errs.detail;
  const messages: string[] = [];
  collectMessages(errs, messages);
  return messages.length ? messages.join(" ") : fallback;
}

/**
 * Fakty o pochodzeniu zadania KPI, które niosą wszystkie trzy widoki (drzewo w projekcie,
 * globalna lista „Zadania KPI", karta zadania). Typ strukturalny, bo każdy z nich ma swój
 * własny wiersz — reguła ma być JEDNA.
 */
export interface KpiOriginFacts {
  origin: KpiActionOrigin;
  /** Autorem jest CEO albo dyrektor techniczny (sam autor — bez względu na miejsce wpisania). */
  by_directorate?: boolean;
  created_by_name?: string | null;
  review_sequence_no?: number | null;
}

/**
 * „Ustalenie dyrekcji" — to, co wyświetlamy na czerwono (zgłoszenie „Wyszczególnienie opisu
 * KPI we FRIDAY", decyzja z 12.09.2026): zadanie wpisane w polu „Ustalenia ze spotkania" na
 * karcie KPI ALBO napisane ręką dyrekcji gdziekolwiek indziej. Dwie drogi, bo obie zdarzają
 * się naprawdę — dyrektor dopisuje ustalenie wprost w tygodniu, a protokolant spisuje
 * ustalenia na karcie spotkania; sama `origin` albo sam autor gubiłyby jeden z tych przypadków.
 */
export function isDirectorateKpiItem(item: KpiOriginFacts): boolean {
  return item.origin === "kpi" || item.by_directorate === true;
}

/**
 * Podpowiedź pod kursorem: skąd wzięło się zadanie i czyja to ręka. Czerwień mówi „to nie
 * twój zapisek" — podpowiedź musi powiedzieć czyj, inaczej kolor jest wróżeniem. Mówi też
 * wprost o statystykach: ustalenie dopisane poza kartą spotkania jest czerwone, ale do
 * liczników KPI nie wchodzi.
 */
export function kpiOriginHint(item: KpiOriginFacts): string {
  const author = item.created_by_name ? ` · autor: ${item.created_by_name}` : "";
  if (item.origin === "kpi") {
    const no = item.review_sequence_no != null ? ` #${item.review_sequence_no}` : "";
    return `Ustalenie ze spotkania KPI${no}${author}`;
  }
  if (item.by_directorate) {
    return `Ustalenie dyrekcji dopisane poza kartą spotkania${author} — nie liczy się do statystyk KPI`;
  }
  return `Zapisek własny${author} — nie liczy się do statystyk KPI`;
}

/**
 * Kto widzi dzwoneczek: dyrektor techniczny i zarząd — lustro bramki `IsTechnicalDirector`
 * z backendu (decyzja usera 12.09.2026). Front tylko zdejmuje przycisk z oczu; o tym, czy
 * ponaglenie wyjdzie, decyduje serwer. Reguła mieszka TUTAJ, bo pytają o nią dwa widoki
 * (drzewo KPI i karta zadania), a dwie kopie rozjechałyby się przy pierwszej zmianie.
 */
export function canPingKpi(
  user: { is_ceo?: boolean; role?: string; is_technical_director?: boolean } | null | undefined,
): boolean {
  if (!user) return false;
  return !!user.is_ceo || (user.role === "director" && !!user.is_technical_director);
}

// ── działki prywatne (WPW) ──────────────────────────────────────────────────────
/**
 * Grupy rejestrowe EGiB uznawane za prywatne: 7 (osoby fizyczne) i 15 (spółka prawa
 * handlowego / inny podmiot). Lustro `jst_assign.PRIVATE_GROUPS` z backendu.
 */
export const PRIVATE_OWNERSHIP_GROUPS = new Set(["7", "15"]);

/**
 * Czy działka ma prywatny kod własności. Grupę liczy BACKEND (`ownership_group` —
 * jst_assign.ownership_group), bo kod poprawiony ręcznie z wypisu bywa zapisany rzymsko
 * („VII"), słownie („osoby fizyczne") albo z zerem wiodącym („07") i front nie może
 * zgadywać po swojemu. `ownership_code` zostaje awaryjnym źródłem dla ładunków bez tego
 * pola (starsze odpowiedzi, dane w testach).
 */
export function isPrivateOwnershipCode(
  p: string | null | undefined | (Pick<Parcel, "ownership_code"> & { ownership_group?: string }),
): boolean {
  const raw = typeof p === "object" && p !== null
    ? (p.ownership_group || p.ownership_code)
    : p;
  return PRIVATE_OWNERSHIP_GROUPS.has((raw ?? "").trim().split(".")[0]);
}

/**
 * Czy działka należy do zbioru WPW (zgody prywatne) — lustro `_is_private_for_wpw`
 * z backendu (wpw_mass/views.py). Wystarczy JEDEN z sygnałów:
 *  - właściciel bez urzędu — klasyczny adresat WPW,
 *  - KOD WŁASNOŚCI grupy prywatnej (7/15) — ręczna poprawka kodu na „7" ma wciągać
 *    działkę na listę nawet wtedy, gdy jej właścicielom przypisano wcześniej urząd
 *    (błędna klasyfikacja z KIEG) albo gdy wypis jeszcze nie wrócił i właścicieli nie ma.
 */
export function isWpwPrivateParcel(
  p: Pick<Parcel, "ownership_code"> & { ownership_group?: string; owners?: Pick<ParcelOwner, "office">[] },
): boolean {
  return (p.owners ?? []).some((o) => !o.office) || isPrivateOwnershipCode(p);
}

// ── strona własności: prywatna vs urzędowa ──────────────────────────────────────
/**
 * Grupy rejestrowe „grunty oddane w użytkowanie wieczyste": 2 (Skarb Państwa), 5 (gmina),
 * 12 (powiat), 14 (województwo) — „w zbiegu z użytkownikiem wieczystym" — oraz 16 (inny
 * właściciel gruntu). Lustro `jst_assign.PERPETUAL_USUFRUCT_GROUPS`. Na takiej działce są
 * DWA podmioty (właściciel gruntu i użytkownik wieczysty) i żaden nie reprezentuje drugiego —
 * zgodę trzeba mieć od obu.
 */
export const PERPETUAL_USUFRUCT_GROUPS = new Set(["2", "5", "12", "14", "16"]);

/** Czy kod własności mówi „grunt w użytkowaniu wieczystym" (grupa jak w isPrivateOwnershipCode). */
export function isPerpetualUsufructCode(
  p: string | null | undefined | (Pick<Parcel, "ownership_code"> & { ownership_group?: string }),
): boolean {
  const raw = typeof p === "object" && p !== null
    ? (p.ownership_group || p.ownership_code)
    : p;
  return PERPETUAL_USUFRUCT_GROUPS.has((raw ?? "").trim().split(".")[0]);
}

/** Kategorie uzgodnienia oznaczające działkę prywatną (lustro services._PRIVATE_LOCATION_CATEGORIES). */
export const PRIVATE_LOCATION_CATEGORIES = new Set(["private_connected", "private_transit"]);

type OwnershipSideParcel = Pick<Parcel, "ownership_code"> & {
  ownership_group?: string;
  location_category?: string;
  owners?: Pick<ParcelOwner, "office" | "kind">[];
};

/**
 * Strona własności działki — lustro `services.parcel_inclusion_kind` z backendu: kanonicznie
 * z `location_category` (private_* = prywatna, każda inna = urzędowa), bo kategoria bierze pod
 * uwagę urząd na właścicielu, kod własności, podgrupy Skarbu Państwa i użytek, a ręczne
 * ustawienie jest nadrzędne. Gdy backend jeszcze nie sklasyfikował działki (pusta kategoria),
 * zostaje dotychczasowa heurystyka: właściciel-osoba fizyczna albo kod własności 7/15 =
 * prywatna.
 */
export function parcelOwnershipSide(p: OwnershipSideParcel): "private" | "office" {
  const category = (p.location_category ?? "").trim();
  if (category) return PRIVATE_LOCATION_CATEGORIES.has(category) ? "private" : "office";
  return (p.owners ?? []).some((o) => o.kind === "person") || isPrivateOwnershipCode(p)
    ? "private"
    : "office";
}

/**
 * Grunt w użytkowaniu wieczystym, którego użytkownik wieczysty jest podmiotem prywatnym
 * (właściciel bez rekordu urzędu). Kategoria takiej działki jest urzędowa (gmina / powiat /
 * Skarb Państwa jest właścicielem gruntu), ale zgodę trzeba mieć TEŻ od użytkownika
 * wieczystego — działka należy więc do OBU kubełków: urzędowego i prywatnego.
 */
export function hasPrivateUsufructuary(p: OwnershipSideParcel): boolean {
  return isPerpetualUsufructCode(p) && (p.owners ?? []).some((o) => !o.office);
}

/** Kubełek PRYWATNY (mapa: kreskowanie „grunt prywatny"; formatki: lista działek prywatnych). */
export function isPrivateSideParcel(p: OwnershipSideParcel): boolean {
  return parcelOwnershipSide(p) === "private" || hasPrivateUsufructuary(p);
}

/** Kubełek URZĘDOWY (formatki: lista działek urzędowych — po jednym zestawie na urząd). */
export function isOfficeSideParcel(p: OwnershipSideParcel): boolean {
  return parcelOwnershipSide(p) === "office";
}

/**
 * Czy działka jest „w grze" w projekcie — lustro `services.project_scope_q` z backendu:
 * pod AKTUALNĄ trasą, dodana ręcznie ALBO oznaczona ręcznie jako WYKORZYSTYWANA
 * (`usage_override === true`). Trzeci sygnał, bo ręczne „wykorzystywana" bije automat:
 * działka oznaczona po tym, jak trasa z niej zeszła, ma `is_used`, a mimo to wypadała
 * z każdej listy tnącej zbiór po samej trasie (WPW, wnioski, lista działek, mapa).
 */
export function isProjectParcel(
  p: Pick<Parcel, "in_current_route" | "is_manual"> & { usage_override?: boolean | null },
): boolean {
  return p.in_current_route || p.is_manual || p.usage_override === true;
}

/**
 * Czy działka w ogóle wchodzi do pracy WPW: prywatna, w grze (pod AKTUALNĄ trasą, dodana
 * ręcznie albo ręcznie wykorzystywana), WYKORZYSTYWANA w projekcie i wymagająca
 * pozyskania zgody.
 *  - `isProjectParcel` — zbiór działek przyłączenia narasta ze wszystkich wariantów trasy,
 *    więc bez tego lista oferowałaby działki, po których trasa już nie idzie; ręczne
 *    „wykorzystywana" (usage_override) trzyma działkę w grze także po zejściu trasy;
 *  - `is_used` — działka „na zapas" (trasa jej nie dotyka, nie stoi na niej obiekt; ręczne
 *    `usage_override` bije automat) nie ma po co iść do negocjatora;
 *  - `consent` — powiadomienie (demontaż) i „nie wymaga" mają własne obiegi, nie pakiet WPW.
 */
export function isWpwCandidateParcel(
  p: Pick<Parcel, "ownership_code" | "is_used" | "in_current_route" | "is_manual" | "access_mode"> & {
    ownership_group?: string;
    usage_override?: boolean | null;
    owners?: Pick<ParcelOwner, "office">[];
  },
): boolean {
  return (
    isProjectParcel(p) &&
    p.is_used &&
    p.access_mode === "consent" &&
    isWpwPrivateParcel(p)
  );
}
